import { z } from 'zod';
import { DomainError } from '../errors.js';
import { compareLocalDate, parseLocalDate } from '../local-date.js';
import type { RaAnswer } from './verification.js';

/**
 * Operasyonel doğrulamanın bulguları ve düzeltici faaliyetleri (K6; ADR 0094). "No" cevabı
 * kendiliğinden ISM uygunsuzluğu değildir: karşılanmamış bir operasyon kontrolüdür ve sınıflanan
 * bir bulgu açar (SS §7; FD §10). Bulgu doğrulamaya bağlıdır; düzeltme "No"yu silmez, yeniden
 * doğrulama ayrı turdur (BR-08). Zorunlu "No"nun bulgusu kapanmadan yetki verilmez (BR-03) ve
 * açık bulgu varken operasyon kapanmaz (BR-09).
 *
 * Durum saklanmaz: bulgunun faaliyetlerinden, değiştirilemez olay izinden
 * (`ra_finding_events`), ofisin kararlarından (`ra_finding_decisions`) ve doğrulamanın sonraki
 * tur cevabından türer. Modül saftır: saat ve gün parametreyle gelir.
 */

/** Bulgu türü (FD §10 "Finding type"; SS §7). */
export const RA_FINDING_TYPES = [
  'operational_constraint',
  'equipment_defect',
  'non_conformity',
  'competency_gap',
  'new_hazard',
  'other',
] as const;
export type RaFindingType = (typeof RA_FINDING_TYPES)[number];

/** Kaynak (FD §10 "Auto from failed check, or manual hazard report"). */
export const RA_FINDING_SOURCES = ['failed_check', 'manual'] as const;
export type RaFindingSource = (typeof RA_FINDING_SOURCES)[number];

/** Bulgunun değiştirilemez olay izi (`ra_finding_events.kind`). */
export const RA_FINDING_EVENTS = [
  'escalated',
  'change_requested',
  'closed',
  'reopened',
  'cancelled',
] as const;
export type RaFindingEvent = (typeof RA_FINDING_EVENTS)[number];

/** Ofisin kararı (`ra_finding_decisions`): DPA'ya yükseltme ve RA değişiklik talebi. */
export const RA_FINDING_DECISION_KINDS = ['escalation', 'change_request'] as const;
export type RaFindingDecisionKind = (typeof RA_FINDING_DECISION_KINDS)[number];

/**
 * Kararın değerleri: DPA güvenli kontrolleri onaylar ya da gemiye geri gönderir (SS §7 "Company
 * escalation: review/approve revised safe controls"); kütüphanenin gözden geçireni değişikliği
 * kabul eder ya da reddeder.
 */
export const RA_FINDING_DECISIONS: Readonly<Record<RaFindingDecisionKind, readonly string[]>> = {
  escalation: ['approved', 'returned'],
  change_request: ['accepted', 'rejected'],
};
export type RaFindingDecision = 'approved' | 'returned' | 'accepted' | 'rejected';

/** Hangi olayın kararı. */
export const RA_FINDING_DECISION_REQUEST: Readonly<Record<RaFindingDecisionKind, RaFindingEvent>> =
  {
    escalation: 'escalated',
    change_request: 'change_requested',
  };

/** Kararın gerekçesi zorunlu mu: geri gönderme ve ret (FD §11 "Return/reject require reason"). */
export function raFindingDecisionNeedsComment(decision: RaFindingDecision): boolean {
  return decision === 'returned' || decision === 'rejected';
}

/** Bulgunun durumu (FD §10 "Disposition": Open / Action in Progress / Corrected / Reverified / Closed). */
export const RA_FINDING_STATUSES = [
  'open',
  'action_in_progress',
  'corrected',
  'reverified',
  'closed',
  'cancelled',
] as const;
export type RaFindingStatus = (typeof RA_FINDING_STATUSES)[number];

/** Yükseltmenin ve değişiklik talebinin durumu (son talebe göre). */
export type RaFindingRequestState =
  | 'none'
  | 'pending'
  | 'approved'
  | 'returned'
  | 'accepted'
  | 'rejected';

/** Düzeltici faaliyetin durumu (DM-17 "Corrective Actions" ile aynı; ADR 0060 §5). */
export const RA_FINDING_ACTION_STATUSES = [
  'open',
  'in_progress',
  'completed',
  'cancelled',
] as const;
export type RaFindingActionStatus = (typeof RA_FINDING_ACTION_STATUSES)[number];

export const RA_FINDING_MAX_ACTIONS = 20;
export const RA_FINDING_TEXT = 2000;
export const RA_FINDING_SHORT_TEXT = 200;
export const RA_FINDING_ACTION_TEXT = 500;

/** Düzeltici faaliyet: bulgunun JSON satırı (ADR 0060 §5 deseni; satır sayısı küçük). */
export interface RaFindingAction {
  /** UUIDv7; sunucu verir. */
  id: string;
  description: string;
  responsible: string | null;
  dueOn: string | null;
  status: RaFindingActionStatus;
  completedOn: string | null;
  /** Kanıt: fotoğraf, belge, test sonucu ya da not (FD §10 "Evidence"). */
  evidence: string | null;
  cancelReason: string | null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const clean = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const v = value.normalize('NFC').trim();
  return v === '' ? null : v;
};

function day(value: unknown, field: string): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value !== 'string') throw new DomainError('INVALID_RA_FINDING', field, field);
  try {
    parseLocalDate(value);
  } catch {
    throw new DomainError('INVALID_RA_FINDING', field, field);
  }
  return value;
}

function limited(value: unknown, max: number, field: string): string | null {
  const v = clean(value);
  if (v !== null && v.length > max) throw new DomainError('TOO_LONG', field, field);
  return v;
}

const text = (max: number) =>
  z
    .string({ error: 'INVALID' })
    .transform((v) => v.normalize('NFC').trim())
    .pipe(z.string().max(max, 'TOO_LONG'))
    .nullish()
    .transform((v) => (v ? v : null));

const localDay = z
  .string({ error: 'INVALID' })
  .refine(
    (v) => {
      try {
        parseLocalDate(v);
        return true;
      } catch {
        return false;
      }
    },
    { message: 'INVALID' },
  )
  .nullish()
  .transform((v) => v ?? null);

/**
 * Bulgunun düzenlenebilir alanları (FD §10): tür, açıklama (zorunlu; ne, nerede, ne zaman),
 * engelleyici mi, acil eylem, sorumlu ve son tarih. Faaliyetler ayrıca (`raFindingActionsOrThrow`).
 */
export const raFindingInputSchema = z.strictObject({
  type: z.enum(RA_FINDING_TYPES, { message: 'INVALID' }),
  description: z
    .string({ error: 'REQUIRED' })
    .transform((v) => v.normalize('NFC').trim())
    .pipe(z.string().min(1, 'REQUIRED').max(RA_FINDING_TEXT, 'TOO_LONG')),
  blocking: z.boolean({ error: 'INVALID' }),
  immediateAction: text(RA_FINDING_TEXT),
  responsible: text(RA_FINDING_SHORT_TEXT),
  dueOn: localDay,
});
export type RaFindingInput = z.output<typeof raFindingInputSchema>;

/** Biçim hatası `INVALID_RA_FINDING` (ayrıntı alan:kod). */
export function raFindingInputOrThrow(input: unknown): RaFindingInput {
  const parsed = raFindingInputSchema.safeParse(input);
  if (!parsed.success) {
    const issues = parsed.error.issues.map((i) => `${i.path.map(String).join('.')}:${i.message}`);
    throw new DomainError(
      'INVALID_RA_FINDING',
      issues.join(','),
      parsed.error.issues[0]?.path.map(String).join('.'),
    );
  }
  return parsed.data;
}

/**
 * Faaliyetler (ADR 0060 §5 ilkesi): en çok 20; açıklama zorunlu (`ACTION_DESCRIPTION_REQUIRED`);
 * tamamlanan satır tamamlanma günü (`COMPLETED_DATE_REQUIRED`, gemi gününden sonra değil) ve
 * kanıt ister (`EVIDENCE_REQUIRED`: "faaliyet kanıtsız tamamlanmış sayılmaz"); iptal gerekçe ister
 * (`CANCEL_REASON_REQUIRED`). Kimlikler tekil. Sonuç sadeleşmiş satırlardır.
 */
export function raFindingActionsOrThrow(input: unknown, today: string): RaFindingAction[] {
  if (input === null || input === undefined) return [];
  if (!Array.isArray(input)) throw new DomainError('INVALID_RA_FINDING', 'actions', 'actions');
  if (input.length > RA_FINDING_MAX_ACTIONS) {
    throw new DomainError('TOO_MANY', 'actions', 'actions');
  }
  const ids = new Set<string>();
  return input.map((raw): RaFindingAction => {
    if (typeof raw !== 'object' || raw === null) {
      throw new DomainError('INVALID_RA_FINDING', 'actions', 'actions');
    }
    const a = raw as Record<string, unknown>;
    if (typeof a.id !== 'string' || !UUID.test(a.id) || ids.has(a.id)) {
      throw new DomainError('INVALID_RA_FINDING', 'actions.id', 'actions.id');
    }
    ids.add(a.id);
    if (!(RA_FINDING_ACTION_STATUSES as readonly unknown[]).includes(a.status)) {
      throw new DomainError('INVALID_RA_FINDING', 'actions.status', 'actions.status');
    }
    const status = a.status as RaFindingActionStatus;
    const description = limited(a.description, RA_FINDING_ACTION_TEXT, 'actions.description');
    if (description === null) throw new DomainError('ACTION_DESCRIPTION_REQUIRED', a.id);
    const evidence = limited(a.evidence, RA_FINDING_ACTION_TEXT, 'actions.evidence');
    const cancelReason = limited(a.cancelReason, RA_FINDING_ACTION_TEXT, 'actions.cancelReason');
    const completedOn = day(a.completedOn, 'actions.completedOn');
    if (status === 'completed') {
      if (evidence === null) throw new DomainError('EVIDENCE_REQUIRED', a.id);
      if (completedOn === null) throw new DomainError('COMPLETED_DATE_REQUIRED', a.id);
      if (compareLocalDate(completedOn, today) > 0) throw new DomainError('FUTURE_DATE', a.id);
    } else if (completedOn !== null) {
      throw new DomainError('INVALID_RA_FINDING', 'actions.completedOn', 'actions.completedOn');
    }
    if (status === 'cancelled' && cancelReason === null) {
      throw new DomainError('CANCEL_REASON_REQUIRED', a.id);
    }
    return {
      id: a.id,
      description,
      responsible: limited(a.responsible, RA_FINDING_SHORT_TEXT, 'actions.responsible'),
      dueOn: day(a.dueOn, 'actions.dueOn'),
      status,
      completedOn,
      evidence,
      cancelReason: status === 'cancelled' ? cancelReason : null,
    };
  });
}

/**
 * "No" cevabının bulgu türü önerisi (SS §7): kontrolsüz yeni tehlike → New Hazard, ekipman →
 * Equipment Defect, brifing ve yetkinlik → Competency Gap; ötekiler Operational Constraint. Tür
 * gemide değiştirilebilir; "No" kendiliğinden Non-Conformity değildir.
 */
export function raFindingTypeForItem(itemKey: string): RaFindingType {
  switch (itemKey) {
    case 'common:no_uncontrolled_hazards':
      return 'new_hazard';
    case 'common:equipment_ready':
      return 'equipment_defect';
    case 'common:crew_briefed':
      return 'competency_gap';
    default:
      return 'operational_constraint';
  }
}

/** Yükseltme açan madde (FD §9 "No opens new-hazard finding and escalation"). */
export const RA_ESCALATING_ITEM = 'common:no_uncontrolled_hazards';

/** Bulgu numarası gemi ve gemi yılı başına: `RF-YYYY-NNN`. */
export function raFindingNumber(
  day: string,
  lastSeq: number,
): { year: number; seq: number; number: string } {
  const { year } = parseLocalDate(day);
  if (!Number.isInteger(lastSeq) || lastSeq < 0) {
    throw new DomainError('VALIDATION_FAILED', `last_seq ${lastSeq}`, 'seq');
  }
  const seq = lastSeq + 1;
  return {
    year,
    seq,
    number: `RF-${String(year).padStart(4, '0')}-${String(seq).padStart(3, '0')}`,
  };
}

export const RA_FINDING_NUMBER = /^RF-\d{4}-\d{3,}$/;

/** Gerekçe isteyen olaylar: hepsi (yükseltme metni, talep, kapanış özeti, yeniden açma, iptal). */
export const RA_FINDING_REASONED_EVENTS: readonly RaFindingEvent[] = RA_FINDING_EVENTS;

// ---------------------------------------------------------------------------------------------
// Durum

export interface RaFindingEventRecord {
  id: string;
  kind: RaFindingEvent;
}

export interface RaFindingDecisionRecord {
  requestId: string;
  kind: RaFindingDecisionKind;
  decision: RaFindingDecision;
}

/** Bulgunun kaynağı ve doğrulamadaki yeri (yeniden doğrulamanın hesabı). */
export interface RaFindingFacts {
  source: RaFindingSource;
  type: RaFindingType;
  blocking: boolean;
  /** Bulgunun açıldığı tur. */
  round: number;
  itemKey: string | null;
  dueOn: string | null;
  actions: readonly RaFindingAction[];
}

export interface RaFindingReverification {
  round: number;
  answer: RaAnswer;
}

export interface RaFindingState {
  status: RaFindingStatus;
  /** Kapanmamış ve iptal edilmemiş. */
  open: boolean;
  escalation: RaFindingRequestState;
  changeRequest: RaFindingRequestState;
  /** Yanıt bekleyen son talebin olay kimliği. */
  pendingEscalation: string | null;
  pendingChangeRequest: string | null;
  /** Yeniden doğrulamanın cevabı (yalnız "No"dan doğan bulgu): sonraki turun son cevabı. */
  reverification: RaFindingReverification | null;
  reverified: boolean;
  overdue: boolean;
}

function requestState(
  events: readonly RaFindingEventRecord[],
  decisions: readonly RaFindingDecisionRecord[],
  kind: RaFindingDecisionKind,
): { state: RaFindingRequestState; pending: string | null } {
  const request = [...events].reverse().find((e) => e.kind === RA_FINDING_DECISION_REQUEST[kind]);
  if (!request) return { state: 'none', pending: null };
  const decision = [...decisions]
    .reverse()
    .find((d) => d.kind === kind && d.requestId === request.id);
  if (!decision) return { state: 'pending', pending: request.id };
  return { state: decision.decision, pending: null };
}

/**
 * Yeniden doğrulama (SS §7 "generate re-verification event rather than overwriting history"):
 * bulgunun maddesinin sonraki turlardaki son cevabı. Yes ya da uygun N/A ise bulgu "Reverified".
 */
export function raFindingReverification(
  facts: Pick<RaFindingFacts, 'source' | 'round' | 'itemKey'>,
  answers: readonly { round: number; itemKey: string; answer: RaAnswer }[],
): RaFindingReverification | null {
  if (facts.source !== 'failed_check' || facts.itemKey === null) return null;
  let found: RaFindingReverification | null = null;
  for (const answer of answers) {
    if (answer.itemKey !== facts.itemKey || answer.round <= facts.round) continue;
    if (!found || answer.round >= found.round)
      found = { round: answer.round, answer: answer.answer };
  }
  return found;
}

/**
 * Bulgunun durumu (FD §10): olay izinden kapanış ve iptal; açıkken faaliyetlerden ve yeniden
 * doğrulamadan Open / Action in Progress / Corrected / Reverified. Gecikme gemi gününe göre: son
 * tarihi geçmiş açık bulgu ya da tamamlanmamış faaliyet.
 */
export function raFindingState(
  facts: RaFindingFacts,
  events: readonly RaFindingEventRecord[],
  decisions: readonly RaFindingDecisionRecord[],
  answers: readonly { round: number; itemKey: string; answer: RaAnswer }[],
  today: string,
): RaFindingState {
  let lifecycle: 'open' | 'closed' | 'cancelled' = 'open';
  for (const event of events) {
    if (event.kind === 'closed') lifecycle = 'closed';
    else if (event.kind === 'cancelled') lifecycle = 'cancelled';
    else if (event.kind === 'reopened') lifecycle = 'open';
  }
  const reverification = raFindingReverification(facts, answers);
  const reverified = reverification !== null && reverification.answer !== 'no';
  const live = facts.actions.filter((a) => a.status !== 'cancelled');
  const done = live.length > 0 && live.every((a) => a.status === 'completed');
  let status: RaFindingStatus;
  if (lifecycle !== 'open') status = lifecycle;
  else if (reverified) status = 'reverified';
  else if (done) status = 'corrected';
  else if (live.some((a) => a.status !== 'open')) status = 'action_in_progress';
  else status = 'open';
  const escalation = requestState(events, decisions, 'escalation');
  const change = requestState(events, decisions, 'change_request');
  const open = lifecycle === 'open';
  const late = (due: string | null) => due !== null && compareLocalDate(due, today) < 0;
  const overdue =
    open &&
    (late(facts.dueOn) ||
      facts.actions.some(
        (a) => (a.status === 'open' || a.status === 'in_progress') && late(a.dueOn),
      ));
  return {
    status,
    open,
    escalation: escalation.state,
    changeRequest: change.state,
    pendingEscalation: escalation.pending,
    pendingChangeRequest: change.pending,
    reverification,
    reverified,
    overdue,
  };
}

/**
 * Kapanışın eksikleri (BR-09; SS §7; FD §10), sırasıyla:
 * - `ACTIONS_OPEN`: tamamlanmamış ya da iptal edilmemiş faaliyet var;
 * - `CORRECTIVE_ACTION_REQUIRED`: kanıtlı tamamlanmış en az bir düzeltici faaliyet yok;
 * - `ESCALATION_PENDING` / `ESCALATION_RETURNED`: DPA'ya yükseltme yanıtsız ya da geri gönderilmiş;
 * - `ESCALATION_REQUIRED`: kontrolsüz yeni tehlike ("No") şirketin onayı olmadan kapanmaz
 *   (FD §9 "No opens new-hazard finding and escalation");
 * - `REVERIFICATION_REQUIRED`: "No"dan doğan engelleyici bulgu, madde sonraki turda Yes ya da
 *   uygun N/A cevaplanmadan kapanmaz ("Mandatory No: … blocked until correction and
 *   re-verification"; şirket onayı engeli aşmaz). Doğrulama iptal edildiyse (operasyon yapılmayacak)
 *   yeniden doğrulama aranmaz.
 */
export function raFindingCloseBlockers(
  facts: RaFindingFacts,
  state: RaFindingState,
  verification: { cancelled: boolean },
): string[] {
  const blockers: string[] = [];
  if (facts.actions.some((a) => a.status === 'open' || a.status === 'in_progress')) {
    blockers.push('ACTIONS_OPEN');
  }
  if (!facts.actions.some((a) => a.status === 'completed')) {
    blockers.push('CORRECTIVE_ACTION_REQUIRED');
  }
  if (state.escalation === 'pending') blockers.push('ESCALATION_PENDING');
  else if (state.escalation === 'returned') blockers.push('ESCALATION_RETURNED');
  else if (
    facts.source === 'failed_check' &&
    facts.itemKey === RA_ESCALATING_ITEM &&
    state.escalation !== 'approved'
  ) {
    blockers.push('ESCALATION_REQUIRED');
  }
  if (
    facts.source === 'failed_check' &&
    facts.blocking &&
    !verification.cancelled &&
    !state.reverified
  ) {
    blockers.push('REVERIFICATION_REQUIRED');
  }
  return blockers;
}

/**
 * Olayın geçerli olup olmadığı (sunucuda denetlenir):
 * - `escalated`, `change_requested`: aynı türde yanıt bekleyen talep yoksa (`REQUEST_PENDING`);
 *   yükseltme yalnız açık bulguda, değişiklik talebi iptal edilmemiş bulguda;
 * - `closed`: açık bulguda ve kapanış eksiksizse (ilk eksik kod);
 * - `reopened`: kapanmış bulguda; doğrulama kapanmışsa yok (BR-09'un kapısı geriye dönmez);
 * - `cancelled`: açık bulguda; "No"dan doğan bulgu yalnız doğrulama iptal edildiyse
 *   (`FINDING_NOT_CANCELLABLE`: güvenlik engeli idari işlemle kaldırılmaz; FD §10).
 */
export function raFindingEventCheck(
  kind: RaFindingEvent,
  facts: RaFindingFacts,
  state: RaFindingState,
  verification: { cancelled: boolean; closed: boolean },
): void {
  switch (kind) {
    case 'escalated':
      if (!state.open) throw new DomainError('INVALID_TRANSITION', `${state.status} → ${kind}`);
      if (state.pendingEscalation !== null) throw new DomainError('REQUEST_PENDING', kind);
      return;
    case 'change_requested':
      if (state.status === 'cancelled') {
        throw new DomainError('INVALID_TRANSITION', `${state.status} → ${kind}`);
      }
      if (state.pendingChangeRequest !== null) throw new DomainError('REQUEST_PENDING', kind);
      return;
    case 'closed': {
      if (!state.open) throw new DomainError('INVALID_TRANSITION', `${state.status} → ${kind}`);
      const [first] = raFindingCloseBlockers(facts, state, verification);
      if (first) throw new DomainError(first);
      return;
    }
    case 'reopened':
      if (state.status !== 'closed') {
        throw new DomainError('INVALID_TRANSITION', `${state.status} → ${kind}`);
      }
      if (verification.closed) throw new DomainError('VERIFICATION_CLOSED');
      return;
    case 'cancelled':
      if (!state.open) throw new DomainError('INVALID_TRANSITION', `${state.status} → ${kind}`);
      if (facts.source === 'failed_check' && !verification.cancelled) {
        throw new DomainError('FINDING_NOT_CANCELLABLE');
      }
      return;
  }
}

/** Arayüzün düğmeleri: durumca geçerli olaylar (yetkiyi sunucu ayrıca denetler). */
export function raFindingEventsAllowed(
  facts: RaFindingFacts,
  state: RaFindingState,
  verification: { cancelled: boolean; closed: boolean },
): RaFindingEvent[] {
  return RA_FINDING_EVENTS.filter((kind) => {
    try {
      raFindingEventCheck(kind, facts, state, verification);
      return true;
    } catch {
      return false;
    }
  });
}

/**
 * Doğrulamanın kapıları (BR-03, BR-09): açık engelleyici bulgu varken yetki verilmez
 * (`FINDINGS_BLOCKING`); açık bulgu varken operasyon kapanmaz (`FINDINGS_OPEN`).
 */
export function raVerificationFindingGate(
  kind: 'authorized' | 'closed',
  findings: readonly { open: boolean; blocking: boolean; number: string }[],
): void {
  if (kind === 'authorized') {
    const blocking = findings.filter((f) => f.open && f.blocking);
    if (blocking.length > 0) {
      throw new DomainError('FINDINGS_BLOCKING', blocking.map((f) => f.number).join(','));
    }
    return;
  }
  const open = findings.filter((f) => f.open);
  if (open.length > 0) {
    throw new DomainError('FINDINGS_OPEN', open.map((f) => f.number).join(','));
  }
}
